import { useState, useRef, useEffect } from 'react'
import { Box, Tooltip, InputBase } from '@mui/material'
import { parseUnits, lotWarning } from './fourToTwelve'

const GREEN = '#15803D'
const AMBER = '#B45309'
const num = (v) => (v == null ? '—' : Math.round(v).toLocaleString())

// FAR-76 §4: a SKU-month's Units. Click (or Enter on focus) to edit; Enter or
// blur saves, Escape cancels. An edited cell has a green border and corner
// marker, and its tooltip gives the Base Plan value and the difference.
//
// Open Item 5 (Tyler, 2026-10-17): what the planner types is saved as typed.
// When it isn't a multiple of the item's lot size the cell is outlined amber
// and the tooltip says so; nothing is rounded.
export default function UnitsCell({ units, baseUnits, edited, review, roundingValue, disabled, onCommit, sx, label }) {
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState('')
  const [invalid, setInvalid] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    if (editing) inputRef.current?.select()
  }, [editing])

  const start = () => {
    if (disabled) return
    setText(units == null ? '' : String(Math.round(units)))
    setInvalid(false)
    setEditing(true)
  }
  const commit = () => {
    const v = parseUnits(text)
    if (v == null) {
      setInvalid(true)
      return
    }
    setEditing(false)
    if (units == null || v !== Math.round(units)) onCommit(v)
  }

  const lot = edited ? lotWarning(Math.round(units ?? 0), roundingValue) : null
  const diff = baseUnits != null && units != null ? Math.round(units - baseUnits) : null
  const tip = edited ? (
    <Box sx={{ fontSize: '0.75rem' }}>
      <div>Edited · Base Plan {num(baseUnits)}</div>
      {diff != null && <div>Difference {diff > 0 ? '+' : ''}{diff.toLocaleString()}</div>}
      {lot && <div style={{ marginTop: 4 }}>Not a multiple of the lot size ({lot.toLocaleString()})</div>}
      {review && <div style={{ marginTop: 4, fontWeight: 600 }}>Needs review: {review} (typed on an earlier Base Plan)</div>}
    </Box>
  ) : ''

  if (editing) {
    return (
      <Box component="td" sx={{ ...sx, p: 0, outline: `2px solid ${invalid ? '#DC2626' : GREEN}`, outlineOffset: -2 }}>
        <InputBase
          inputRef={inputRef}
          value={text}
          onChange={(e) => { setText(e.target.value); setInvalid(false) }}
          onBlur={() => (parseUnits(text) == null ? setEditing(false) : commit())}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit() }
            if (e.key === 'Escape') { e.preventDefault(); setEditing(false) }
          }}
          inputProps={{ 'aria-label': `${label} units`, inputMode: 'numeric', style: { textAlign: 'center', padding: '2px 0', fontSize: '0.8rem', fontWeight: 600 } }}
          sx={{ width: '100%' }}
        />
      </Box>
    )
  }

  return (
    <Tooltip title={tip} arrow disableInteractive>
      <Box
        component="td"
        tabIndex={disabled ? -1 : 0}
        role={disabled ? undefined : 'button'}
        aria-label={disabled ? undefined : `Edit ${label} units`}
        data-edited={edited ? 'true' : undefined}
        data-lot-warning={lot ? 'true' : undefined}
        data-needs-review={review ? 'true' : undefined}
        onClick={start}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'F2') { e.preventDefault(); start() } }}
        sx={{
          ...sx,
          fontWeight: 600,
          cursor: disabled ? 'default' : 'text',
          position: 'relative',
          '&:hover': disabled ? undefined : { bgcolor: 'action.hover' },
          ...(edited && {
            ...(review && { bgcolor: '#FEF3C7' }), // FAR-79: amber fill = needs review
            outline: `2px solid ${lot || review ? AMBER : GREEN}`,
            outlineOffset: -2,
            // corner marker
            '&::after': {
              content: '""', position: 'absolute', top: 0, right: 0,
              borderTop: `7px solid ${lot || review ? AMBER : GREEN}`, borderLeft: '7px solid transparent',
            },
          }),
        }}
      >
        {num(units)}
      </Box>
    </Tooltip>
  )
}
